// Budget Pacing Dashboard (S015)
import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { BudgetPacingAlert } from '../components/BudgetPacingAlert';

interface PacingData {
  campaignId: string;
  campaignName: string;
  budgetTotal: number;
  budgetSpent: number;
  daysTotal: number;
  daysElapsed: number;
  pacingStatus: 'on_track' | 'underspending' | 'overspending';
}

export const BudgetPacingPage: React.FC = () => {
  const [pacing, setPacing] = useState<PacingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadPacing();
  }, []);

  const loadPacing = async () => {
    try {
      const response = await apiClient.get('/budget-pacing');
      setPacing(response.data.campaigns || []);
    } catch (err) {
      console.error('Failed to load pacing:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPacing = filter === 'all' 
    ? pacing 
    : pacing.filter(p => p.pacingStatus === filter);

  const stats = {
    total: pacing.length,
    onTrack: pacing.filter(p => p.pacingStatus === 'on_track').length,
    underspending: pacing.filter(p => p.pacingStatus === 'underspending').length,
    overspending: pacing.filter(p => p.pacingStatus === 'overspending').length,
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-black dark:text-white mb-6">
        Budget Pacing
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-boxdark rounded-lg p-4 border">
          <p className="text-sm text-bodydark">Total Campaigns</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-success bg-opacity-10 rounded-lg p-4 border border-success">
          <p className="text-sm text-success">On Track</p>
          <p className="text-2xl font-bold text-success">{stats.onTrack}</p>
        </div>
        <div className="bg-warning bg-opacity-10 rounded-lg p-4 border border-warning">
          <p className="text-sm text-warning">Underspending</p>
          <p className="text-2xl font-bold text-warning">{stats.underspending}</p>
        </div>
        <div className="bg-danger bg-opacity-10 rounded-lg p-4 border border-danger">
          <p className="text-sm text-danger">Overspending</p>
          <p className="text-2xl font-bold text-danger">{stats.overspending}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {['all', 'on_track', 'underspending', 'overspending'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg transition ${
              filter === status
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-boxdark border border-stroke'
            }`}
          >
            {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <div className="space-y-4">
          {filteredPacing.map(data => (
            <BudgetPacingAlert key={data.campaignId} {...data} />
          ))}
        </div>
      )}
    </div>
  );
};
